/* eslint-disable no-underscore-dangle */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';

import { WithCheckControlUpdates } from '../../constants/form-field.constant';
import { AutonumericDirective } from '../../directives/autonumeric.directive';
import { MessageService } from '../../services/message-service.service';

class CustomFieldErrorMatcher implements ErrorStateMatcher {
  constructor(private customControl: FormControl) {}
  isErrorState(): boolean {
    return this.customControl && this.customControl.touched && this.customControl.invalid;
  }
}

@Component({
  selector: 'app-currency-input',
  templateUrl: './app-currency-input.component.html',
  styleUrls: ['./app-currency-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppCurrencyInputComponent),
      multi: true
    }
  ]
})
export class AppCurrencyInputComponent
  extends WithCheckControlUpdates(self => self.control)
  implements ControlValueAccessor, OnChanges
{
  readonly defaultOptions = {
    digitGroupSeparator: ',',
    decimalCharacter: '.',
    decimalCharacterAlternative: '.',
    minimumValue: '0',
    maximumValue: 999999.9999,
    decimalPlaces: 4,
    decimalPlacesShownOnBlur: 4,
    watchExternalChanges: true
  };

  @ViewChild(AutonumericDirective) input: AutonumericDirective;
  @Input()
  get isRequired(): boolean {
    return this._required;
  }
  set isRequired(value: boolean) {
    this._required = value;
  }
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: boolean) {
    this._readonly = value;
  }
  @Input()
  get maxLength(): number {
    return this._maxLength || null;
  }
  set maxLength(value: number) {
    this._maxLength = value;
  }

  constructor(private messageService: MessageService, cd: ChangeDetectorRef) {
    super(cd);
  }
  @Input() public placeholder: string;
  @Input() public testId: string;
  @Input() public hideLabel?: boolean;
  @Input() public inputLabel?: string;
  @Input() public inputHint?: string;
  @Input() public warningMessage?: string;
  @Input() public isWarningVisible?: boolean;
  @Input() public shouldShowCurrencySign = true;
  @Input() public shouldShowMinusSign = false;
  @Input() public inputClass?: string;
  @Input() public isTouched: string;

  // tslint:disable-next-line:no-input-rename
  @Input('formControl') public control: FormControl;
  @Input() public maxValue = 999999.9999;
  @Input() public decimals = 4;
  @Input() public useRequiredProperty = true;

  // tslint:disable-next-line:variable-name
  private _required: boolean;
  private _readonly: boolean;
  private _maxLength: number;

  currencyControlOptions = Object.assign(this.defaultOptions);

  onChange: any = () => {};
  onTouched: any = () => {};

  public errorMatcher() {
    return new CustomFieldErrorMatcher(this.control);
  }

  public getErrorMessage(): string {
    return this.control && this.control.errors ? this.messageService.getControlErrorMessage(this.control.errors) : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.control && this.input) {
      if (this.control && this.control.value) {
        this.input.instance.set(this.control.value);
      } else {
        this.input.instance.clear();
      }
    }
    if (changes.decimals || changes.maxValue) {
      this.currencyControlOptions = Object.assign(this.defaultOptions);
      this.currencyControlOptions.decimalPlaces = this.decimals;
      this.currencyControlOptions.decimalPlacesShownOnBlur = this.decimals;
      this.currencyControlOptions.maximumValue = this.maxValue;
    }
  }

  writeValue(value: any): void {}

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
